import { useEffect, useRef, useState } from "react";

import type { NeuralCore } from "../../lib/neural";
import { useTheme } from "../../lib/theme";

/**
 * The AI Core canvas.
 *
 * Everything here is about the canvas being unable to hurt the page:
 *
 *   **It is loaded after first paint, not before.** three.js is ~130 kB
 *   gzipped — more than everything else on this site combined. A static
 *   import puts that in the critical path and the hero headline waits behind
 *   a decoration. The dynamic import means the type renders immediately and
 *   the field fades in underneath it a moment later.
 *
 *   **It is `aria-hidden` and it is not the heading.** The h1 is real DOM
 *   text sitting on top. A screen reader gets a normal document; this
 *   contributes nothing to it, which is correct, because it says nothing.
 *
 *   **It stops when it is not being looked at.** Offscreen or a hidden tab
 *   and the RAF loop is cancelled outright. An animation running behind a
 *   backgrounded tab is someone's battery.
 *
 *   **Every failure is silent.** No WebGL, a shader that will not compile, a
 *   blocked context — the catch leaves the hero exactly as it renders without
 *   this component. There is no error state because there is nothing to tell
 *   the visitor; the page was complete before the canvas arrived.
 */

/** Below this the device is a phone: fewer nodes, and far fewer edges, since
 *  edge count grows with node count and lines are the expensive part. */
const MOBILE_BREAKPOINT = 768;

const BUDGET = {
  // Few nodes, well separated. A column of 30 barely-visible dots reads as
  // texture; a column of 15 distinct glowing nodes reads as a *layer*, and
  // the layers are the whole point.
  mobile: { nodeCount: 70, pulseCount: 30 },
  desktop: { nodeCount: 120, pulseCount: 56 },
} as const;

function budgetForViewport() {
  return window.innerWidth < MOBILE_BREAKPOINT ? BUDGET.mobile : BUDGET.desktop;
}

export function AICore({ className = "" }: { className?: string }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const coreRef = useRef<NeuralCore | null>(null);
  const [ready, setReady] = useState(false);
  const theme = useTheme();

  // Deliberately not in the mount effect's dependency list. Rebuilding the
  // scene on a theme change would re-seed 320 nodes, recompute every edge and
  // relink three shader programs to change two colours — the core exposes
  // setTheme precisely so this can be a uniform write instead.
  useEffect(() => {
    coreRef.current?.setTheme(theme);
  }, [theme]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    // An object rather than a `let` boolean. The flag is written by the
    // cleanup function and read after an `await`, which is control flow the
    // compiler cannot see — with a plain boolean it narrows the check to
    // "always false" and the guard looks like dead code.
    const lifecycle = { cancelled: false };
    const reduced = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    const teardown: (() => void)[] = [];

    void (async () => {
      try {
        const { createNeuralCore } = await import("../../lib/neural");
        if (lifecycle.cancelled) return;

        const core = createNeuralCore(canvas, {
          reducedMotion: reduced,
          // Read from the DOM rather than the closure: this runs after an
          // await, and the visitor may have hit the toggle in between.
          theme:
            document.documentElement.dataset.theme === "dark"
              ? "dark"
              : "light",
          ...budgetForViewport(),
        });
        coreRef.current = core;
        core.resize();

        if (reduced) {
          // One frame. The composition is information; the movement is not.
          core.renderStill();
        } else {
          core.start();
        }
        setReady(true);

        const onResize = () => {
          core.resize();
          if (reduced) core.renderStill();
        };
        window.addEventListener("resize", onResize);
        teardown.push(() => {
          window.removeEventListener("resize", onResize);
        });

        if (!reduced) {
          const onPointerMove = (event: PointerEvent) => {
            const box = canvas.getBoundingClientRect();
            core.setPointer(
              ((event.clientX - box.left) / box.width) * 2 - 1,
              -(((event.clientY - box.top) / box.height) * 2 - 1),
            );
          };
          // On window, not the canvas: the canvas sits behind the headline, so
          // listening on it would drop every event the text is over — which is
          // the middle of the screen, where the pointer actually is.
          window.addEventListener("pointermove", onPointerMove, {
            passive: true,
          });
          teardown.push(() => {
            window.removeEventListener("pointermove", onPointerMove);
          });

          // A click in the field sends an activation wave through the
          // network — nodes flash as the front passes. Same normalisation as
          // pointermove, and swallowed by the core under reduced motion.
          const onPointerDown = (event: PointerEvent) => {
            const box = canvas.getBoundingClientRect();
            core.burst(
              ((event.clientX - box.left) / box.width) * 2 - 1,
              -(((event.clientY - box.top) / box.height) * 2 - 1),
            );
          };
          window.addEventListener("pointerdown", onPointerDown, {
            passive: true,
          });
          teardown.push(() => {
            window.removeEventListener("pointerdown", onPointerDown);
          });

          const onVisibility = () => {
            if (document.hidden) core.stop();
            else core.start();
          };
          document.addEventListener("visibilitychange", onVisibility);
          teardown.push(() => {
            document.removeEventListener("visibilitychange", onVisibility);
          });

          const io = new IntersectionObserver(
            ([entry]) => {
              if (entry.isIntersecting) core.start();
              else core.stop();
            },
            { threshold: 0 },
          );
          io.observe(canvas);
          teardown.push(() => {
            io.disconnect();
          });
        }
      } catch (error) {
        // Silent for the visitor — the page is complete without this and
        // there is nothing useful to tell them. NOT silent for the developer:
        // swallowing it entirely is how a stale Vite dep cache hid a dead
        // canvas behind a page that otherwise looked fine. `import.meta.env.DEV`
        // is statically replaced, so this whole block is stripped from the
        // production bundle.
        if (import.meta.env.DEV) {
          console.warn("[AICore] the neural field failed to start:", error);
        }
      }
    })();

    return () => {
      lifecycle.cancelled = true;
      for (const off of teardown) off();
      coreRef.current?.dispose();
      coreRef.current = null;
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      className={`pointer-events-none transition-opacity duration-1000 ease-out ${
        ready ? "opacity-100" : "opacity-0"
      } ${className}`}
    />
  );
}
